/**
 * Role Session Expired Modal
 *
 * Shown when an elevated role session expires. Displays a countdown timer
 * and offers the user two options:
 * - Extend Session: Re-activates the role (requires 2FA)
 * - Go to Dashboard: Deactivates the role and redirects to home
 *
 * After the countdown reaches 0, automatically triggers dismiss (redirects to dashboard).
 *
 * Note: The parent (DashboardLayout) conditionally renders this component
 * only when expiredRoleInfo is non-null, so it always mounts with fresh state.
 */

import { useState, useEffect, useCallback } from 'react';
import { Modal } from '../ui/Modal';
import { ShieldExclamationIcon } from '@heroicons/react/24/outline';
import clsx from 'clsx';

const AUTO_REDIRECT_SECONDS = 10;

export interface RoleSessionExpiredModalProps {
  /** Whether the modal is open */
  isOpen: boolean;
  /** Role code that expired (e.g., "admin") */
  roleCode: string;
  /** Role name that expired (e.g., "Administrator") */
  roleName: string;
  /** Callback when user dismisses (Go to Dashboard or countdown expires) */
  onDismiss: () => void;
  /** Callback when user wants to extend the session */
  onExtend: () => void;
  /** Whether extend action is loading */
  isExtendLoading?: boolean;
}

export const RoleSessionExpiredModal = ({
  isOpen,
  roleCode,
  roleName,
  onDismiss,
  onExtend,
  isExtendLoading = false,
}: RoleSessionExpiredModalProps) => {
  const [countdown, setCountdown] = useState(AUTO_REDIRECT_SECONDS);
  const [error, setError] = useState<string | null>(null);

  // Countdown timer
  useEffect(() => {
    if (!isOpen || isExtendLoading) return;

    const interval = setInterval(() => {
      setCountdown((prev) => {
        if (prev <= 1) {
          clearInterval(interval);
          return 0;
        }
        return prev - 1;
      });
    }, 1000);

    return () => clearInterval(interval);
  }, [isOpen, isExtendLoading]);

  // Auto-dismiss when countdown reaches 0
  useEffect(() => {
    if (countdown === 0 && isOpen && !isExtendLoading) {
      onDismiss();
    }
  }, [countdown, isOpen, isExtendLoading, onDismiss]);

  const handleExtend = useCallback(async () => {
    setError(null);
    try {
      onExtend();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to extend session');
    }
  }, [onExtend]);

  // Calculate progress for the visual indicator
  const progressPercent = (countdown / AUTO_REDIRECT_SECONDS) * 100;

  return (
    <Modal isOpen={isOpen} onClose={onDismiss} size="sm" closeOnOverlayClick={!isExtendLoading}>
      <Modal.Header>
        <div className="flex items-center gap-3">
          <div className="flex-shrink-0 w-10 h-10 rounded-full bg-amber-100 dark:bg-amber-900/30 flex items-center justify-center">
            <ShieldExclamationIcon className="h-6 w-6 text-amber-600 dark:text-amber-400" />
          </div>
          <div>
            <h3 className="text-lg font-semibold" style={{ color: 'var(--color-text-primary)' }}>
              Elevated Session Expired
            </h3>
            <p className="text-sm" style={{ color: 'var(--color-text-secondary)' }}>
              {roleName} ({roleCode})
            </p>
          </div>
        </div>
      </Modal.Header>
      <Modal.Body>
        <div className="space-y-4">
          <p className="text-sm" style={{ color: 'var(--color-text-secondary)' }}>
            Your elevated <strong>{roleCode}</strong> session has expired. You will be redirected to
            the dashboard to continue using the application with standard permissions.
          </p>

          {/* Countdown indicator */}
          <div className="space-y-2">
            <div className="flex items-center justify-between text-sm">
              <span style={{ color: 'var(--color-text-secondary)' }}>Redirecting in</span>
              <span
                className={clsx(
                  'font-semibold text-lg tabular-nums',
                  countdown <= 3
                    ? 'text-red-600 dark:text-red-400'
                    : 'text-amber-600 dark:text-amber-400'
                )}
              >
                {countdown}s
              </span>
            </div>

            {/* Progress bar */}
            <div className="w-full h-1.5 rounded-full overflow-hidden" style={{ backgroundColor: 'var(--color-border)' }}>
              <div
                className="h-full rounded-full transition-all duration-1000 ease-linear bg-amber-500"
                style={{ width: `${progressPercent}%` }}
              />
            </div>
          </div>

          {/* Info box */}
          <div
            className="rounded-lg p-3 border"
            style={{
              backgroundColor: 'var(--color-bg-primary)',
              borderColor: 'var(--color-border)',
            }}
          >
            <p className="text-sm" style={{ color: 'var(--color-text-secondary)' }}>
              You can extend your elevated session by verifying your identity again.
              A new verification code will be sent to your registered email.
            </p>
          </div>

          {/* Error message */}
          {error && (
            <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-3">
              <p className="text-sm text-red-600 dark:text-red-400">{error}</p>
            </div>
          )}
        </div>
      </Modal.Body>
      <Modal.Footer>
        <button
          type="button"
          onClick={onDismiss}
          disabled={isExtendLoading}
          className="px-4 py-2 text-sm font-medium rounded-lg border focus:outline-none focus:ring-2 focus:ring-gray-500 disabled:opacity-50 disabled:cursor-not-allowed"
          style={{
            color: 'var(--color-text-primary)',
            backgroundColor: 'var(--color-bg-raised)',
            borderColor: 'var(--color-border)',
          }}
        >
          Go to Dashboard
        </button>
        <button
          type="button"
          onClick={handleExtend}
          disabled={isExtendLoading}
          className={clsx(
            'px-4 py-2 text-sm font-medium text-white rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500',
            'bg-primary-600 hover:bg-primary-700',
            'disabled:opacity-50 disabled:cursor-not-allowed'
          )}
        >
          {isExtendLoading ? 'Sending code...' : 'Extend Session'}
        </button>
      </Modal.Footer>
    </Modal>
  );
};
